import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Card from '../../components/Card/Card';
import Button from '../../components/Button/Button';
import Input from '../../components/Input/Input';
import Loading from '../../components/Loading/Loading';
import api from '../../services/api';
import { Invite, InviteCreate } from '../../types';
import { getErrorMessage } from '../../utils/helpers';
import './InviteForm.scss';

const InviteForm: React.FC = () => {
  const { eventId, inviteId } = useParams<{ eventId: string; inviteId?: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isEdit = Boolean(inviteId);

  const [title, setTitle] = useState('');
  const [guests, setGuests] = useState<{ name: string; id: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(isEdit);
  const [error, setError] = useState('');

  const fetchInvite = useCallback(async () => {
    if (!user?.uuid || !eventId || !inviteId) return;

    try {
      setFetchLoading(true);
      const invite = await api.getInvite(user.uuid, eventId, inviteId);
      setTitle(invite.title);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setFetchLoading(false);
    }
  }, [user, eventId, inviteId]);

  useEffect(() => {
    if (isEdit && inviteId) {
      fetchInvite();
    }
  }, [isEdit, inviteId, fetchInvite]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.uuid || !eventId) return;

    setError('');
    setLoading(true);

    try {
      if (isEdit && inviteId) {
        const inviteData: Invite = {
          title: title.trim(),
        };
        await api.updateInvite(user.uuid, eventId, inviteId, inviteData);
      } else {
        const inviteData: InviteCreate = {
          title: title.trim(),
          guests: guests.length > 0 
            ? guests.map(g => ({ name: g.name.trim() })).filter(g => g.name)
            : undefined,
        };
        await api.createInvite(user.uuid, eventId, inviteData);
      }

      navigate(`/events/${eventId}`);
    } catch (err) {
      setError(getErrorMessage(err));
      setLoading(false);
    }
  };

  const addGuest = () => {
    setGuests([...guests, { name: '', id: Date.now().toString() }]);
  };

  const removeGuest = (id: string) => {
    setGuests(guests.filter(g => g.id !== id));
  };

  const updateGuestName = (id: string, name: string) => {
    setGuests(guests.map(g => g.id === id ? { ...g, name } : g));
  };

  const handleCancel = () => {
    navigate(`/events/${eventId}`);
  };

  if (fetchLoading) {
    return <Loading message="Loading invite..." />;
  }

  return (
    <div className="invite-form">
      <div className="container">
        <Card>
          <h1>{isEdit ? 'Edit Invite' : 'Create New Invite'}</h1>
          <p className="form-description">
            {isEdit
              ? 'Update the invite title. To manage guests, use the invite details page.'
              : 'Create an invite group with a title and optionally add guests. Guests will provide email and dietary details when they RSVP.'}
          </p>

          {error && <div className="error-message">{error}</div>}

          <form onSubmit={handleSubmit}>
            <Input
              label="Invite Title"
              name="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Smith Family, John & Jane, Work Colleagues"
              required
            />

            {!isEdit && (
              <div className="guests-section">
                <div className="guests-section-header">
                  <h3>Guests (Optional)</h3>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="small"
                    onClick={addGuest}
                  >
                    + Add Guest
                  </Button>
                </div>
                <p className="section-description">
                  Add guests to this invite now, or add them later from the invite details page.
                </p>

                {guests.length > 0 && (
                  <div className="guests-list">
                    {guests.map((guest, index) => (
                      <div key={guest.id} className="guest-item">
                        <div className="guest-number">{index + 1}</div>
                        <Input
                          label=""
                          name={`guest-${guest.id}`}
                          type="text"
                          value={guest.name}
                          onChange={(e) => updateGuestName(guest.id, e.target.value)}
                          placeholder="Guest name"
                        />
                        <Button
                          type="button"
                          variant="danger"
                          size="small"
                          onClick={() => removeGuest(guest.id)}
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="form-actions">
              <Button type="button" variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Saving...' : isEdit ? 'Update Invite' : 'Create Invite'}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default InviteForm;
